import { useEffect } from 'react';
import { useViewerStoreHandle } from '../store/context';

/**
 * Global keyboard shortcuts: find (Ctrl/Cmd-F), zoom (Ctrl/Cmd +/-/0),
 * and Escape to close the find bar. Uses the store handle so the listener is
 * stable and reads the latest state imperatively.
 */
export function useKeyboardShortcuts(canFind: boolean): void {
  const store = useViewerStoreHandle();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      const state = store.getState();

      if (mod && (e.key === 'f' || e.key === 'F')) {
        // Document find only applies when the preview pane is visible; in
        // edit-only mode let the keystroke fall through to the editor/browser.
        if (state.status !== 'ready' || !canFind) return;
        e.preventDefault();
        state.setFindOpen(true);
        return;
      }
      if (mod && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        state.zoomIn();
        return;
      }
      if (mod && e.key === '-') {
        e.preventDefault();
        state.zoomOut();
        return;
      }
      if (mod && e.key === '0') {
        e.preventDefault();
        state.zoomReset();
        return;
      }
      if (e.key === 'Escape' && state.findOpen) {
        e.preventDefault();
        state.setFindOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [store, canFind]);
}
